// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL CONFIG TAB - UrbanQ v1.9.6
// Configuracion de categorias, subcategorias y metodos de pago
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiSettings, FiPlus, FiEdit2, FiTrash2, FiSave, FiX,
    FiRefreshCw, FiFolder, FiTag, FiCalendar
} from 'react-icons/fi';

export default function FinancialConfigTab({ token, apiUrl, scope, onNotification }) {
    const [loading, setLoading] = useState(true);
    const [incomeCategories, setIncomeCategories] = useState([]);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [config, setConfig] = useState(null);

    // Estados de edicion
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingSubsText, setEditingSubsText] = useState('');
    const [newCategory, setNewCategory] = useState(null);
    const [newSubsText, setNewSubsText] = useState('');

    const defaultColors = [
        '#667eea', '#764ba2', '#48bb78', '#38a169', '#4299e1',
        '#ed8936', '#e53e3e', '#9f7aea', '#ed64a6', '#38b2ac'
    ];

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);

            const [incomeRes, expenseRes, configRes] = await Promise.all([
                fetch(`${apiUrl}/api/financial/categories?type=income&scope=${scope}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${apiUrl}/api/financial/categories?type=expense&scope=${scope}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${apiUrl}/api/financial/config?scope=${scope}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const incomeData = await incomeRes.json();
            const expenseData = await expenseRes.json();
            const configData = await configRes.json();

            if (incomeData.success) setIncomeCategories(incomeData.data || []);
            if (expenseData.success) setExpenseCategories(expenseData.data || []);
            if (configData.success) setConfig(configData.data || {});

        } catch (err) {
            console.error('Error fetching config:', err);
            onNotification?.('Error al cargar configuracion', 'error');
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token, scope, onNotification]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Parsear texto de subcategorias (coma como separador)
    const parseSubsText = (text) => {
        return text.split(',').map(s => s.trim()).filter(s => s.length > 0).map(s => ({ name: s }));
    };

    // CRUD de categorias
    const handleSaveCategory = async (category, type, subsText) => {
        try {
            const subcategories = type === 'expense' ? parseSubsText(subsText || '') : [];

            const url = category.id
                ? `${apiUrl}/api/financial/categories/${category.id}`
                : `${apiUrl}/api/financial/categories`;

            const method = category.id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...category,
                    type,
                    scope,
                    subcategories
                })
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.(category.id ? 'Categoria actualizada' : 'Categoria creada', 'success');
                setEditingCategory(null);
                setEditingSubsText('');
                setNewCategory(null);
                setNewSubsText('');
                fetchCategories();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error saving category:', err);
            onNotification?.('Error al guardar categoria', 'error');
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm('¿Eliminar esta categoria? Los registros asociados mantendran la categoria pero no podras seleccionarla para nuevos registros.')) return;

        try {
            const response = await fetch(`${apiUrl}/api/financial/categories/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.('Categoria eliminada', 'success');
                fetchCategories();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error deleting category:', err);
            onNotification?.(err.message || 'Error al eliminar', 'error');
        }
    };

    // Iniciar edicion de categoria existente
    const handleStartEdit = (cat) => {
        setEditingCategory(cat);
        const subsText = (cat.subcategories || []).map(s => s.name).join(', ');
        setEditingSubsText(subsText);
    };

    // Guardar configuracion
    const handleSaveConfig = async () => {
        try {
            const response = await fetch(`${apiUrl}/api/financial/config`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...config, scope })
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.('Configuracion guardada', 'success');
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error saving config:', err);
            onNotification?.('Error al guardar', 'error');
        }
    };

    // Inicializar categorias por defecto
    const handleInitCategories = async () => {
        if (!window.confirm('¿Crear las categorias por defecto? Continuar?')) return;

        try {
            const response = await fetch(`${apiUrl}/api/financial/init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ scope })
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.(result.initialized ? 'Categorias inicializadas' : 'Las categorias ya existen', 'success');
                fetchCategories();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error initializing:', err);
            onNotification?.('Error al inicializar', 'error');
        }
    };

    if (loading) {
        return (
            <div className="urbanq-finanzas-loading">
                <div className="urbanq-finanzas-spinner"></div>
            </div>
        );
    }

    const renderCategoryForm = (category, type, isNew) => {
        const subsText = isNew ? newSubsText : editingSubsText;
        const setSubsText = isNew ? setNewSubsText : setEditingSubsText;
        const setCategory = isNew ? setNewCategory : setEditingCategory;
        const onCancel = isNew
            ? () => { setNewCategory(null); setNewSubsText(''); }
            : () => { setEditingCategory(null); setEditingSubsText(''); };

        return (
            <div className="urbanq-finanzas-category-form">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <input
                        type="text"
                        placeholder="Nombre de categoria"
                        value={category.name || ''}
                        onChange={(e) => setCategory(prev => ({ ...prev, name: e.target.value }))}
                        className="urbanq-finanzas-form-input"
                        autoFocus
                    />
                    <select
                        value={category.color || defaultColors[0]}
                        onChange={(e) => setCategory(prev => ({ ...prev, color: e.target.value }))}
                        className="urbanq-finanzas-form-select"
                        style={{ background: category.color || defaultColors[0], color: 'white' }}
                    >
                        {defaultColors.map(color => (
                            <option key={color} value={color} style={{ background: color, color: 'white' }}>
                                {color}
                            </option>
                        ))}
                    </select>
                </div>

                {type === 'expense' && (
                    <div style={{ marginBottom: '10px' }}>
                        <input
                            type="text"
                            placeholder="Subcategorias separadas por coma: Electricidad, Agua, Gas"
                            value={subsText}
                            onChange={(e) => setSubsText(e.target.value)}
                            className="urbanq-finanzas-form-input"
                        />
                        <small className="urbanq-finanzas-form-hint">
                            Escribe y separa con comas. Ejemplo: Electricidad, Agua, Gas
                        </small>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="urbanq-finanzas-btn secondary sm" onClick={onCancel}>
                        <FiX /> Cancelar
                    </button>
                    <button
                        className="urbanq-finanzas-btn success sm"
                        onClick={() => handleSaveCategory(category, type, subsText)}
                        disabled={!category.name?.trim()}
                    >
                        <FiSave /> Guardar
                    </button>
                </div>
            </div>
        );
    };

    const renderCategoryList = (categories, type) => (
        <div className="urbanq-finanzas-category-list">
            {/* Formulario de nueva categoria */}
            {newCategory?.type === type && renderCategoryForm(newCategory, type, true)}

            {categories.map(cat => (
                <React.Fragment key={cat.id}>
                    {editingCategory?.id === cat.id ? (
                        renderCategoryForm(editingCategory, type, false)
                    ) : (
                        <div className="urbanq-finanzas-category-item">
                            <div
                                className="urbanq-finanzas-category-color"
                                style={{ background: cat.color }}
                            >
                                <FiFolder />
                            </div>
                            <div className="urbanq-finanzas-category-info">
                                <span className="urbanq-finanzas-category-name">{cat.name}</span>
                                {cat.subcategories && cat.subcategories.length > 0 && (
                                    <span className="urbanq-finanzas-category-subs">
                                        {cat.subcategories.map(s => s.name).join(', ')}
                                    </span>
                                )}
                            </div>
                            <div className="urbanq-finanzas-category-actions">
                                <button
                                    className="urbanq-finanzas-row-action"
                                    onClick={() => handleStartEdit(cat)}
                                    title="Editar"
                                >
                                    <FiEdit2 />
                                </button>
                                {!cat.is_default && (
                                    <button
                                        className="urbanq-finanzas-row-action delete"
                                        onClick={() => handleDeleteCategory(cat.id)}
                                        title="Eliminar"
                                    >
                                        <FiTrash2 />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </React.Fragment>
            ))}

            {categories.length === 0 && !newCategory && (
                <div className="urbanq-finanzas-config-empty">
                    Sin categorias. Agrega una nueva o usa "Inicializar Categorias".
                </div>
            )}
        </div>
    );

    return (
        <div className="urbanq-finanzas-config-tab">
            <div className="urbanq-finanzas-section">
                <div className="urbanq-finanzas-section-header">
                    <h2><FiSettings /> Configuracion del Sistema Financiero</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="urbanq-finanzas-btn secondary" onClick={handleInitCategories}>
                            <FiRefreshCw /> Inicializar Categorias
                        </button>
                    </div>
                </div>

                <div className="urbanq-finanzas-config-grid">
                    {/* Categorias de Ingresos */}
                    <div className="urbanq-finanzas-config-card">
                        <h3>
                            <span><FiTag /> Categorias de Ingresos</span>
                            <button
                                className="urbanq-finanzas-btn primary sm"
                                onClick={() => { setNewCategory({ type: 'income', color: defaultColors[0] }); setNewSubsText(''); }}
                                disabled={!!newCategory || !!editingCategory}
                            >
                                <FiPlus />
                            </button>
                        </h3>
                        {renderCategoryList(incomeCategories, 'income')}
                    </div>

                    {/* Categorias de Gastos */}
                    <div className="urbanq-finanzas-config-card">
                        <h3>
                            <span><FiTag /> Categorias de Gastos</span>
                            <button
                                className="urbanq-finanzas-btn primary sm"
                                onClick={() => { setNewCategory({ type: 'expense', color: defaultColors[2] }); setNewSubsText(''); }}
                                disabled={!!newCategory || !!editingCategory}
                            >
                                <FiPlus />
                            </button>
                        </h3>
                        {renderCategoryList(expenseCategories, 'expense')}
                    </div>
                </div>
            </div>

            {/* Año Fiscal */}
            <div className="urbanq-finanzas-section">
                <h2><FiCalendar /> Año Fiscal</h2>

                <div className="urbanq-finanzas-config-fiscal">
                    <div className="urbanq-finanzas-filter-group">
                        <span className="urbanq-finanzas-filter-label">Inicio del año fiscal:</span>
                        <select
                            className="urbanq-finanzas-select"
                            value={config?.fiscal_year_start || 1}
                            onChange={(e) => setConfig(prev => ({
                                ...prev,
                                fiscal_year_start: parseInt(e.target.value)
                            }))}
                        >
                            <option value={1}>Enero</option>
                            <option value={2}>Febrero</option>
                            <option value={3}>Marzo</option>
                            <option value={4}>Abril</option>
                            <option value={5}>Mayo</option>
                            <option value={6}>Junio</option>
                            <option value={7}>Julio</option>
                            <option value={8}>Agosto</option>
                            <option value={9}>Septiembre</option>
                            <option value={10}>Octubre</option>
                            <option value={11}>Noviembre</option>
                            <option value={12}>Diciembre</option>
                        </select>
                    </div>

                    <div className="urbanq-finanzas-filter-group">
                        <span className="urbanq-finanzas-filter-label">Moneda:</span>
                        <select
                            className="urbanq-finanzas-select"
                            value={config?.currency || 'USD'}
                            onChange={(e) => setConfig(prev => ({
                                ...prev,
                                currency: e.target.value
                            }))}
                        >
                            <option value="USD">USD - Dólar Americano</option>
                            <option value="EUR">EUR - Euro</option>
                            <option value="COP">COP - Peso Colombiano</option>
                            <option value="PEN">PEN - Sol Peruano</option>
                            <option value="CLP">CLP - Peso Chileno</option>
                            <option value="MXN">MXN - Peso Mexicano</option>
                        </select>
                    </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                    <button className="urbanq-finanzas-btn primary" onClick={handleSaveConfig}>
                        <FiSave /> Guardar Configuracion
                    </button>
                </div>
            </div>
        </div>
    );
}
